import Script from 'next/script';
import { useEffect, useRef } from 'react';

import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';

import { PFLEGIA_TRUSTPILOT_REVIEW_URL, PRAXIA_TRUSTPILOT_REVIEW_URL } from '@shared/application/constants';
import { TrackingEventName } from '@shared/application/marketing/events/tracking-event-name';
import { useTracking } from '@shared/application/tracking.service';

const TRUSTPILOT_BOOTSTRAP_URL = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
const TRUSTPILOT_REVIEW_STARS = '4,5';

export const enum TrustpilotWidgetType {
  Default = '53aa8807dec7e10d38f59f32',
  MicroCombo = '5419b6ffb0d04a076446a9af',
  MicroStar = '5419b732fbfb950b10de65e5',
  Horizontal = '5406e65db0d04a09e042d5fc',
  ReviewCollector = '56278e9abfbbba0bdcd568bc',
}

const TRUSTPILOT_BY_BRAND = {
  pflegia: {
    businessUnitId: process.env.NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID,
    reviewUrl: PFLEGIA_TRUSTPILOT_REVIEW_URL,
    defaultType: TrustpilotWidgetType.Default,
  },
  praxia: {
    businessUnitId: process.env.NEXT_PUBLIC_TRUSTPILOT_BUSINESS_UNIT_ID_PRAXIA,
    reviewUrl: PRAXIA_TRUSTPILOT_REVIEW_URL,
    defaultType: TrustpilotWidgetType.ReviewCollector,
  },
};

interface TrustpilotWidgetProps {
  businessUnitId?: string;
  type?: TrustpilotWidgetType;
  theme?: 'light' | 'dark';
}

const CHECK_INTERVAL = 500;

const getStyleHeight = (type: TrustpilotWidgetType) => {
  if (type === TrustpilotWidgetType.MicroCombo || type === TrustpilotWidgetType.MicroStar || type === TrustpilotWidgetType.Horizontal) {
    return '24px';
  }

  if (type === TrustpilotWidgetType.ReviewCollector) return '75px';

  return '120px';
};

// Added businessUnitId as a prop to make it easier to test the component
const TrustpilotWidget: React.FC<TrustpilotWidgetProps> = ({ businessUnitId: businessUnitIdProp, type, theme = 'light' }) => {
  const { isPraxia } = useFlavorFromRoute();
  const ref = useRef<HTMLDivElement>(null);
  const { track } = useTracking();
  const hasTrackedRef = useRef<boolean>(false);

  const trustpilot = TRUSTPILOT_BY_BRAND[isPraxia ? 'praxia' : 'pflegia'];
  const businessUnitId = businessUnitIdProp ?? trustpilot.businessUnitId;
  const templateId = type || trustpilot.defaultType;

  useEffect(() => {
    if (!ref.current) return;

    if (window.Trustpilot) window.Trustpilot.loadFromElement(ref.current, true);

    const checkForWidget = setInterval(() => {
      if (hasTrackedRef.current || !ref.current) {
        clearInterval(checkForWidget);
        return;
      }

      if (ref.current.querySelector('iframe')) {
        clearInterval(checkForWidget);

        const observer = new IntersectionObserver(
          entries => {
            if (entries[0].isIntersecting && !hasTrackedRef.current) {
              track(TrackingEventName.TrustpilotWidgetViewed);
              hasTrackedRef.current = true;
              observer.disconnect();
            }
          },
          { threshold: 1 },
        );

        observer.observe(ref.current);
      }
    }, CHECK_INTERVAL);

    return () => {
      clearInterval(checkForWidget);
    };
  }, [track, businessUnitId, templateId]);

  if (!businessUnitId) return null;

  return (
    <>
      <div
        ref={ref}
        className='trustpilot-widget'
        data-locale='de-DE'
        data-template-id={templateId}
        data-businessunit-id={businessUnitId}
        data-style-height={getStyleHeight(templateId)}
        data-style-width='100%'
        data-stars={TRUSTPILOT_REVIEW_STARS}
        data-testid='trustpilot-widget'
        data-review-languages='de'
        data-theme={theme}>
        <a href={trustpilot.reviewUrl} target='_blank' rel='noopener' className='sr-only'>
          Trustpilot
        </a>
      </div>
      <Script id='trustpilot' type='text/javascript' async src={TRUSTPILOT_BOOTSTRAP_URL} strategy='afterInteractive' />
    </>
  );
};

export default TrustpilotWidget;
